import React, { useState } from "react";
import Select from "react-select";
import { FaBug, FaUpload } from "react-icons/fa";
import Sidebar from "../components/SideBar";
import { auth } from "../firebase";
import { uploadIssue } from "./ReportIssuesSection/ReportAnIssue";

const categoryOptions = [
  { value: "bug", label: "Bug / Error" },
  { value: "ui", label: "UI / Design Issue" },
  { value: "performance", label: "Performance" },
  { value: "data", label: "Data Issue" },
  { value: "feature", label: "Feature Request" },
  { value: "other", label: "Other" },
];

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "white",
    borderColor: state.isFocused ? "#007AFF" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 122, 255, 0.2)" : "none",
    borderRadius: "0.5rem",
    padding: "2px",
    minHeight: "44px",
    cursor: "pointer",
    "&:hover": { borderColor: "#007AFF" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "white",
    border: "1px solid #E5E7EB",
    borderRadius: "0.5rem",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    zIndex: 20,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#007AFF"
      : state.isFocused
      ? "#F3F4F6"
      : "white",
    color: state.isSelected ? "white" : "#1F2937",
    cursor: "pointer",
    padding: "10px 14px",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#1F2937",
    fontWeight: 500,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9CA3AF",
  }),
};

const ReportIssue = () => {
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    description: "",
    file: null,
  });
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, file }));
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.category || !formData.description) {
      alert("Please fill all required fields.");
      return;
    }
    const currentUser = auth.currentUser;
    setSubmitting(true);
    try {
      const userData = {
        uid: currentUser?.uid,
        email: currentUser?.email,
        userRole: currentUser?.userRole || "vendor",
      };

      await uploadIssue(formData, userData);

      alert("Your issue has been submitted successfully!");
      setFormData({ title: "", category: "", description: "", file: null });
      setPreview(null);
    } catch (err) {
      console.error("Error submitting issue:", err);
      alert("Failed to submit issue. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <main className="flex-1 ml-80 p-8 lg:p-10 transition-all duration-300 max-w-full md:max-w-[calc(100%-20rem)]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white w-full rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 hover:shadow-md transition-all duration-300">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 border-b pb-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-xl">
                <FaBug className="text-blue-600 text-2xl" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
                  Report an Issue
                </h1>
                <p className="text-gray-500 text-sm sm:text-base">
                  Help us improve by reporting bugs or issues.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {/* Title */}
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                Issue Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter issue title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50 text-sm sm:text-base"
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                Category <span className="text-red-500">*</span>
              </label>
              <Select
                options={categoryOptions}
                styles={customStyles}
                placeholder="Select a category"
                value={categoryOptions.find(
                  (opt) => opt.value === formData.category
                )}
                onChange={(selected) =>
                  setFormData((prev) => ({ ...prev, category: selected.value }))
                }
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                rows="5"
                placeholder="Describe the issue in detail..."
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm sm:text-base"
                required
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                Attach Screenshot (optional)
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-3">
                <label className="cursor-pointer flex items-center justify-center px-5 py-3 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-100 transition-all font-medium text-sm sm:text-base">
                  <FaUpload className="mr-2 text-blue-600" />
                  Upload File
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                </label>

                {formData.file && (
                  <span className="text-xs sm:text-sm text-gray-600 truncate max-w-[200px] sm:max-w-xs">
                    {formData.file.name}
                  </span>
                )}
              </div>

              {preview && (
                <div className="mt-4">
                  <img
                    src={preview}
                    alt="Preview"
                    className="rounded-lg border border-gray-200 w-full sm:w-64 h-40 object-cover shadow-sm"
                  />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className={`px-6 sm:px-8 py-3 rounded-lg text-white font-semibold transition-all duration-200 text-sm sm:text-base ${
                  submitting
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-md active:scale-95"
                }`}
              >
                {submitting ? "Submitting..." : "Submit Issue"}
              </button>
            </div>
          </form>
        </div>
        </div>
      </main>
    </div>
  );
};

export default ReportIssue;
